import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Constants } from '../lib/const/constants';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { encryptPayload } from '../utils/encryption';

export default function ManualTransferDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const [copiedField, setCopiedField] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const login_workspace = JSON.parse(
    sessionStorage.getItem('login_workspaces'),
  );
  const membership_id = login_workspace[0].memberships.urn;
  const clearBankCustomerWalletNumber = sessionStorage.getItem(
    'clearBankCustomerWalletNumber',
  );
  const clearBankCustomerSortCode = sessionStorage.getItem(
    'clearBankCustomerSortCode',
  );

  const selectedbeneficiary = state.name || state.bank_account_name || '';
  const selectedbeneficiaryCode =
    state.from_currency === 'GBP'
      ? clearBankCustomerSortCode ||
        Constants.ACCOUNT_DETAILS[0]?.GBP?.[0]?.['Sort Code']
      : '-';
  const selectedbeneficiaryBankAccoutNumber =
    state.from_currency === 'GBP'
      ? clearBankCustomerWalletNumber ||
        Constants.ACCOUNT_DETAILS?.[0]?.GBP?.[0]?.['Account Number']
      : '-';
  const selectedbeneficiaryType = state.type || state.bene_type || '';
  const selectedbeneficiaryId =
    state.id || state.bank_account_id || state.contactId || '';
  const selectedbeneficiaryBankAccountId = state.bank_account_id || '';
  const selectedbeneficiaryCountry = state.country || '';
  const selectedbeneficiaryBeneType = state.bene_type || '';
  const selectedbeneficiaryBankType = state.bank_type || '';

  const details = {
    reference: membership_id,
    beneficiary: selectedbeneficiary,
    Code: selectedbeneficiaryCode,
    accountNumber: selectedbeneficiaryBankAccoutNumber,
    accountName: 'fxmaster limited',
    iban: state.iban || '',
    swift: state.swift || '',
    bankName: state.bank_account_name || state.bankName || '',
    currency: state.from_currency || '',
    noteTitle: 'Action Required',
    noteBody:
      'Hi! Just one step to complete your transfer. Please make a transfer in your name. To secure this great rate, please pay within the next 3 hours.',
  };

  useEffect(() => {
    if (!details.beneficiary) {
      toast.warn('Beneficiary name missing — check details');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyToClipboard = async (text, fieldKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copied to clipboard!');
    } catch (err) {
      console.error('copy failed', err);
      toast.error('Copy failed! Please copy manually.');
    }
  };

  const shareDetails = () => {
    const text = getShareText();
    if (navigator.share) {
      navigator
        .share({ title: 'Bank transfer details', text })
        .then(() => toast.success('Shared successfully'))
        .catch((err) => {
          console.warn('share failed or cancelled', err);
          copyToClipboard(text, 'share');
        });
    } else {
      copyToClipboard(text, 'share');
    }
  };

  const getShareText = () => {
    return [
      `Account Name:${details.accountName}`,
      `Account Number:${details.accountNumber}`,
      `Sort Code:${details.Code}`, // ⚠️ fix case (Code not code)
      `Currency:${details.currency}`,
      `Reference:${details.reference}`,
    ].join('\n');
  };

  const paymentConfirmation  = async () => {
  const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
  const token = sessionStorage.getItem('login_token');
  const staff_login_id = sessionStorage.getItem('staff_login_id');

  const encryptedPayload = await encryptPayload({
    contact_id: state.contactId,
    workspace_id: login_workspaces_id,
    reason_code: state.transferReason,
    full_name: selectedbeneficiary,
    sort_code: selectedbeneficiaryCode,
    createTransaction: true,
    currency: state.currency,
    account_number: selectedbeneficiaryBankAccoutNumber,
    type: 'local',
    currency_cloud_balance: 0,
    clear_bank_balance: 0,
    total_balance: 0,
    beneficiaryId: selectedbeneficiaryId,
    reference: state.ref || membership_id,
    reason: state.transferReason,
    staff_login_id,
    status: 'waiting for funds',
    flow: 'sendFlow',
    transfer_fee: state.transfer_fee,
    service_fee: state.service_fee,
    method: 'manual',
    platform: 'web',
    extraParamValidation: 'trustedfromweb',
  });

  const resp = await axios.post(
    Constants.BASE_URL + 'API-FX-240-SEND-FLOW',
    encryptedPayload,
    {
      headers: {
        fx_key: Constants.SUBSCRIPTION_KEY,
        Authorization: 'Bearer ' + JSON.parse(token),
      },
    },
  );

  return resp?.data?.data?.id;
};

  const handleContinue = async() => {
    if (submitting) return;
    if (!selectedbeneficiary) {
      toast.error('Beneficiary missing');
      return;
    }
    if (!details.accountNumber) {
      toast.error('Account number missing');
      return;
    }


    setSubmitting(true); 

     let transactionId;
  try {
    transactionId = await paymentConfirmation();
  } catch (err) {
    
    const errorMessage = err.response?.data?.message || err.message || 'Something went wrong. Please try again.';
    toast.error(errorMessage);
    setSubmitting(false); 
    return;
  }


    toast.info('Continuing to confirmation...');
    navigate('/PaymentSuccess', {
      replace: true,
      state: {
        name: selectedbeneficiary,
        ifsc: selectedbeneficiaryCode,
        account: selectedbeneficiaryBankAccoutNumber,
        type: selectedbeneficiaryType,
        id: selectedbeneficiaryId,
        bank_account_name: selectedbeneficiary,
        currencyid: state.currencyid,
        currency: state.currency,
        amount: state.amount || state.transferAmount || '',
        bank_account_id: selectedbeneficiaryBankAccountId,
        country: selectedbeneficiaryCountry,
        bene_type: selectedbeneficiaryBeneType,
        bank_type: selectedbeneficiaryBankType,
        contactId: state.contactId,
        transactionId,
      },
    });
  };

  const rows = [
    {
      key: 'accountName',
      label: 'Account Name',
      value: details.accountName,
      copyVal: details.accountName,
    },
    {
      key: 'sortCode',
      label: 'Sort Code',
      value: details.Code || '-',
      copyVal: details.Code,
    },
    {
      key: 'accountNumber',
      label: 'Account Number',
      value: details.accountNumber,
      copyVal: details.accountNumber,
    },
    {
      key: 'reference',
      label: 'Reference Number',
      value: details.reference,
      copyVal: details.reference,
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #f8fafc, #eff6ff4d, #eef2ff66)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 700,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 32,
        }}
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
        />

        {/* Header */}
        <header
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '28px 20px 12px',
            position: 'relative',
          }}
        >
          <button
            style={{
              position: 'absolute',
              left: 20,
              top: 32,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#1a2e4a',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "'Inter', sans-serif",
              padding: '4px 0',
            }}
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>
              <ArrowLeft className="w-5 h-5" />
            </span>{' '}
            <b>Back</b>
          </button>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#1a2e4a',
              margin: '0 0 4px',
              textAlign: 'center',
            }}
          >
            Bank Transfer Details
          </h2>
        </header>

        {/* Outer card */}
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            margin: '8px 16px 0',
            padding: 16,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Inner card 1: Action Required */}
          <div
            style={{
              border: '1.5px solid #e2e8f0',
              borderRadius: 12,
              padding: '18px 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 30,
                height: 30,
                borderRadius: '50%',
                border: '1.5px solid #c5cdd8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="#6b7a8d"
                  strokeWidth="1.8"
                />
                <path
                  d="M12 8v4M12 16h.01"
                  stroke="#6b7a8d"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#1a2e4a',
                  marginBottom: 5,
                }}
              >
                {details.noteTitle}
              </div>
              <div style={{ fontSize: 13, color: '#6b7a8d', lineHeight: 1.6 }}>
                {details.noteBody}
              </div>
            </div>
          </div>

          {/* Inner card 2: Bank Details */}
          <div
            style={{
              border: '1.5px solid #e2e8f0',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 16px',
                borderBottom: '1px solid #f0f4f8',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#1a2e4a',
                }}
              >
                Bank Details
              </span>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <button
                  onClick={() => copyToClipboard(getShareText(), 'share')}
                  style={{
                    padding: '5px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: copiedField === 'share' ? '#16a34a' : '#fff',
                    color: copiedField === 'share' ? '#fff' : '#1a2e4a',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.18s',
                    minWidth: 56,
                  }}
                >
                  {copiedField === 'share' ? '✓ Done' : 'Copy'}
                </button>

                <button
                  onClick={shareDetails}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7a8d',
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share
                </button>
              </div>
            </div>

            {/* Detail Rows */}
            {rows.map((row, idx) => (
              <div
                key={row.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 16px',
                  borderBottom:
                    idx < rows.length - 1 ? '1px solid #f0f4f8' : 'none',
                }}
              >
                <span
                  style={{ fontSize: 13, color: '#6b7a8d', fontWeight: 400 }}
                >
                  {row.label}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: '#1a2e4a' }}
                  >
                    {row.value}
                  </span>
                  <button
                    onClick={() => copyToClipboard(row.copyVal, row.key)}
                    style={{
                      padding: '5px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      background: copiedField === row.key ? '#16a34a' : '#fff',
                      color: copiedField === row.key ? '#fff' : '#1a2e4a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      transition: 'all 0.18s',
                      minWidth: 56,
                    }}
                  >
                    {copiedField === row.key ? '✓ Done' : 'Copy'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Confirm Payment Button — separate outside cards */}
        <div style={{ padding: '20px 16px 0' }}>
          <button
            onClick={handleContinue}
            style={{
              width: '100%',
              padding: 16,
              background: '#E8650A',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              letterSpacing: 0.2,
              transition: 'opacity 0.18s',
            }}
          >
            {submitting ? 'Processing...' : (<>Confirm payment <ArrowRight className='w-5 h-5 -ml-1'/></>)}
          </button>
        </div>

        {/* Note — separate outside cards */}
        <div
          style={{
            margin: '12px 16px 0',
            background: '#fff8f3',
            border: '1px solid #e4c4a7',
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect
                x="4"
                y="3"
                width="13"
                height="19"
                rx="2"
                stroke="#c05c1a"
                strokeWidth="1.5"
              />
              <path
                d="M8 7h8M8 11h8M8 15h5"
                stroke="#c05c1a"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#1a2e4a',
                marginBottom: 3,
              }}
            >
              Note
            </div>
            <div style={{ fontSize: 12, color: '#6b7a8d', lineHeight: 1.5 }}>
              Must include the reference number when making the transfer
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ManualTransferDetails.propTypes = {
  reference: PropTypes.string,
  bankDetails: PropTypes.object,
};
